import { useAuth, useSession, useSessionList, useUser } from "@clerk/clerk-react";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import SwitchUserModal from "@/components/impersonation/SwitchUserModal";
import { useOrganization } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";

/** Height of the impersonation banner — matches the CSS token --imp-bar-height. */
export const IMP_BAR_HEIGHT = 44;

/**
 * ImpersonationBanner — fixed wine-red top bar shown while a GFG admin is
 * viewing the app as another user.
 *
 * Renders only when `useAuth().actor` is set (= Clerk actor session active).
 * "Stop" first explicitly restores the original admin session using `setActive()`,
 * then calls `session.remove()` to clean up the impersonated session, then reloads at "/" so
 * every cache restarts as the admin and `LandingRedirect` lands them on the GFG dashboard —
 * not on whatever client page they were impersonating from. The swap button opens the
 * SwitchUserModal for quick user-switching without stopping first.
 */
export default function ImpersonationBanner() {
  const { actor } = useAuth();
  const { user } = useUser();
  const { session } = useSession();
  const { sessions, setActive } = useSessionList();
  const [switchOpen, setSwitchOpen] = useState(false);

  const { organizationId } = usePermissions();
  // Only fetch the org if we are actively impersonating
  const { data: org } = useOrganization(actor ? organizationId : null);

  // Only render when impersonation is active
  if (!actor) return null;

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Unknown";
  const orgName = org?.name || null;

  const handleStop = async () => {
    if (!session || !setActive) return;

    try {
      // Find the original admin session using the actor's sub claim, falling back to any non-actor session
      const adminSession =
        sessions?.find((s) => s.user?.id === session.actor?.sub) ||
        sessions?.find((s) => s.id !== session.id && !s.actor);

      if (adminSession) {
        // Switch back to the admin session
        await setActive({ session: adminSession.id });
        // Clean up the impersonated session
        await session.remove();
      } else {
        // Fallback if the admin session was somehow lost
        await session.remove();
      }
    } catch (error) {
      console.error("Failed to stop impersonation cleanly", error);
    }
    // Back to the admin's own home (LandingRedirect → /gfg-dashboard), with a clean slate.
    window.location.assign("/");
  };

  return (
    <>
      <Box
        component="div"
        role="status"
        aria-label="Impersonation active"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: IMP_BAR_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "24px",
          bgcolor: "var(--color-good-wine)",
          color: "var(--color-white)",
          fontSize: "14px",
          fontFamily: "var(--font-lato)",
          zIndex: 200,
        }}
      >
        {/* Eye icon */}
        <VisibilityIcon sx={{ fontSize: 18, flexShrink: 0 }} />

        {/* "Impersonating: Name | Company" */}
        <Typography
          component="span"
          sx={{
            fontSize: "14px",
            fontFamily: "var(--font-lato)",
            color: "inherit",
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Impersonating:{" "}
          <strong style={{ fontWeight: 700 }}>
            {displayName}
            {orgName ? ` | ${orgName}` : ""}
          </strong>
        </Typography>

        {/* Swap user button */}
        <IconButton
          onClick={() => setSwitchOpen(true)}
          title="Switch user"
          aria-label="Switch to a different user"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            color: "var(--color-white)",
            transition: "background 0.15s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 19 }} />
        </IconButton>

        {/* Stop button — pushed to the right */}
        <Box
          component="button"
          onClick={handleStop}
          sx={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            bgcolor: "rgba(255,255,255,0.12)",
            color: "var(--color-white)",
            px: "14px",
            py: "6px",
            borderRadius: "6px",
            border: "none",
            fontFamily: "var(--font-lato)",
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
            cursor: "pointer",
            transition: "background 0.15s",
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
          Stop
        </Box>
      </Box>

      {/* Switch user modal */}
      <SwitchUserModal open={switchOpen} onClose={() => setSwitchOpen(false)} />
    </>
  );
}
